import { createApi, retry, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import select from 'store/select';
import AtlasQueryError from './AtlasQueryError';

/* *************************************************************************************** */
/* Atlas Rest API Docs https://learn.microsoft.com/en-us/rest/api/maps/?view=rest-maps-1.0 */
/* *************************************************************************************** */

const baseQuery = async (args, api, extraOptions) => {
  const atlasFetchClient = retry(
    fetchBaseQuery({
      baseUrl: 'https://atlas.microsoft.com',
    }),
    { maxRetries: 3 }
  );

  const result = await atlasFetchClient(args, api, extraOptions);

  if (result.error) {
    // global error handling for all Fusion endpoints
    const error = new AtlasQueryError(result, args, api.endpoint, api.type);
    error.report();
  }

  return result;
};

const baseQueryWithSubscriptionKey = async (args, api, extraOptions) => {
  const subscriptionKey = select.tokens.azureMaps(api.getState());

  // Handle both string and object query formats
  if (typeof args === 'string') {
    // args is just the endpoint string
    args += args.includes('?')
      ? `&subscription-key=${subscriptionKey}`
      : `?subscription-key=${subscriptionKey}`;
  } else if (typeof args === 'object') {
    const url = args.url || '';
    args.url = url.includes('?')
      ? `${url}&subscription-key=${subscriptionKey}`
      : `${url}?subscription-key=${subscriptionKey}`;
  }

  return baseQuery(args, api, extraOptions);
};

const atlasApi = createApi({
  reducerPath: 'atlas',
  baseQuery: baseQueryWithSubscriptionKey,
  keepUnusedDataFor: 60 * 60, // 60 minutes
  endpoints: (builder) => ({
    getSearchFuzzy: builder.query({
      query: (arg = {}) => {
        const defaultParams = {
          'api-version': 1,
          typeahead: true,
          countrySet: ['US', 'PR', 'VI'],
        };

        let url = '/search/fuzzy/json';

        let argParams;
        if (typeof arg === 'string') {
          // when string is passed, treat it as "query" query param.
          argParams = { query: arg };
        } else {
          argParams = arg;
        }
        const searchParams = new URLSearchParams({ ...defaultParams, ...argParams });
        url += `?${searchParams.toString()}`;
        return url;
      },
    }),
  }),
});

export default atlasApi;

export const hooks = {
  useGetSearchFuzzyQuery: atlasApi.useGetSearchFuzzyQuery,
};
